import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { LogIn, Sparkles, UserPlus } from "lucide-react";
import { Page, useIsDesktop } from "@/components/kit";
import { ContactFields } from "@/pages/EditContact";
import { GuestHeader } from "@/pages/GuestScan";
import { useScanDraft } from "@/context/auth";
import { getGuest, setGuest } from "@/lib/guest";

const KEYS = { name: "fullName" };

function SaveCta() {
  return (
    <div className="rounded-[20px] border border-[#C7C7FA] bg-[#F5F5FF] p-5" data-testid="guest-save-cta">
      <div className="flex items-center gap-2 text-[#0000EC]"><Sparkles className="w-5 h-5" /><h2 className="text-[18px] font-extrabold" data-testid="guest-save-title">Want to save this contact?</h2></div>
      <p className="mt-1.5 text-[14px] text-[#475569]" data-testid="guest-save-text">Create a free account or log in to save this contact and get 10 free scans.</p>
      <div className="mt-4 flex flex-col gap-2 [&>a]:text-[14px] sm:[&>a]:text-[15px] [&>a]:whitespace-nowrap">
        <Link to="/register" className="t-btn t-btn-primary gap-2" data-testid="guest-create-account"><UserPlus className="w-4 h-4" />Create Account & Save Contact</Link>
        <Link to="/login" className="t-btn t-btn-outline gap-2" data-testid="guest-login"><LogIn className="w-4 h-4" />Log In to Save Contact</Link>
      </div>
    </div>
  );
}

export default function GuestReview() {
  const { draft, setDraft } = useScanDraft();
  const src = draft?.guest ? draft : getGuest();
  const [form, setForm] = useState(() => src?.extracted ?? {});
  const [url, setUrl] = useState(null);
  const desktop = useIsDesktop();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const flagged = useMemo(() => Object.fromEntries(Object.entries(src?.confidence ?? {}).filter(([, v]) => v !== "high").map(([k]) => [k, true])), [src]);

  useEffect(() => {
    if (!draft?.localFront) return;
    const u = URL.createObjectURL(draft.localFront);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [draft?.localFront]);
  useEffect(() => {
    const g = getGuest();
    if (!g) return;
    setGuest({ ...g, extracted: form });
    if (draft?.guest) setDraft((d) => ({ ...d, extracted: form }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  if (!src) return <Navigate to="/try" replace />;
  return (
    <div className="min-h-screen bg-white">
      <GuestHeader />
      <Page className="pt-6 lg:pt-10">
        <h1 className="text-[28px] lg:text-4xl font-extrabold" data-testid="guest-review-title">Review Contact</h1>
        <p className="text-[15px] text-[#475569] mb-6">Check the details below. Nothing is saved until you sign in.</p>
        <div className="lg:grid lg:grid-cols-[0.9fr_1.4fr] lg:gap-10">
          <div className="lg:sticky lg:top-6 self-start space-y-4">
            {(url || src?.thumb) && <img src={url || src.thumb} alt="Card" className="w-full rounded-[18px] border border-[#E2E8F0] aspect-[1.7/1] object-cover" data-testid="guest-card-image" />}
            {src.warning && <p className="text-[13px] text-[#B54708]">{src.warning}</p>}
            {desktop && <SaveCta />}
          </div>
          <div className="mt-6 lg:mt-0">
            {!desktop && <div className="mb-6"><SaveCta /></div>}
            <ContactFields form={form} setForm={setForm} keys={KEYS} flagged={flagged} prefix="guest" />
          </div>
        </div>
      </Page>
    </div>
  );
}
